import React, { useState, useEffect } from 'react';
import { X, Save, FileText, Tag, Users, FolderOpen } from 'lucide-react';
import Button from './Button';

interface DocumentEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: DocumentMetadata) => void;
  documentName: string;
  documentId: string;
  initialData?: DocumentMetadata;
}

interface DocumentMetadata {
  name: string;
  description: string;
  documentType: string;
  documentSubtype: string;
  project?: string;
  team: string;
  tags: string;
  isPublic: boolean;
  syncWith: string;
}

const DocumentEditModal: React.FC<DocumentEditModalProps> = ({
  isOpen,
  onClose,
  onSave,
  documentName,
  documentId,
  initialData
}) => {
  const [formData, setFormData] = useState<DocumentMetadata>({
    name: '',
    description: '',
    documentType: '',
    documentSubtype: '',
    project: '',
    team: '',
    tags: '',
    isPublic: false,
    syncWith: 'local'
  });

  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      if (initialData) {
        setFormData(initialData);
      } else {
        setFormData({
          name: documentName,
          description: '',
          documentType: '',
          documentSubtype: '',
          project: '',
          team: '',
          tags: '',
          isPublic: false,
          syncWith: 'local'
        });
      }
    }
  }, [isOpen, initialData, documentName]);

  const documentTypes = [
    { value: 'Document Stratégiques', label: 'Document Stratégiques' },
    { value: 'Opérationnels', label: 'Opérationnels' },
    { value: 'Normatifs', label: 'Normatifs' },
    { value: 'Techniques', label: 'Techniques' }
  ];

  const getSubtypes = (type: string) => {
    const subtypes: { [key: string]: string[] } = {
      'Document Stratégiques': [
        'Plan d\'activité Annuel',
        'Propositions Techniques',
        'Proposition financière',
        'Rapport d\'activité'
      ],
      'Opérationnels': [
        'Procédures',
        'Manuels',
        'Guides',
        'Checklists'
      ],
      'Normatifs': [
        'Contrats',
        'Factures',
        'Devis',
        'Règlements'
      ],
      'Techniques': [
        'Manuels Techniques',
        'Spécifications Techniques',
        'Documentation Technique',
        'Procédures Techniques'
      ]
    };
    return subtypes[type] || [];
  };

  const projects = [
    { value: 'projet-1', label: 'Projet Alpha' },
    { value: 'projet-2', label: 'Projet Beta' },
    { value: 'projet-3', label: 'Projet Gamma' }
  ];

  const teams = [
    { value: 'equipe-1', label: 'Équipe Marketing' },
    { value: 'equipe-2', label: 'Équipe Technique' },
    { value: 'equipe-3', label: 'Équipe Commerciale' }
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    
    try {
      // Simulation de la sauvegarde
      await new Promise(resolve => setTimeout(resolve, 1000));
      onSave(formData);
      onClose();
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleTypeChange = (type: string) => {
    setFormData(prev => ({
      ...prev,
      documentType: type,
      documentSubtype: '' // Reset subtype when type changes
    }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <FileText className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Modifier le document</h3>
              <p className="text-sm text-gray-500">ID: {documentId}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Nom du document */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Nom du document <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Nom du document"
              required
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              rows={3}
              placeholder="Description du document..."
            />
          </div>

          {/* Classification du document */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Tag className="w-4 h-4" />
              Classification du document
            </h4>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Type de document */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Type de document <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.documentType}
                  onChange={(e) => handleTypeChange(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                >
                  <option value="">Sélectionnez un type...</option>
                  {documentTypes.map(type => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Sous-type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Sous-type <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.documentSubtype}
                  onChange={(e) => setFormData(prev => ({ ...prev, documentSubtype: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                  disabled={!formData.documentType}
                >
                  <option value="">Sélectionnez un sous-type...</option>
                  {getSubtypes(formData.documentType).map(subtype => (
                    <option key={subtype} value={subtype}>
                      {subtype}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Projet et Équipe */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Projet */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Projet (optionnel)
              </label>
              <select
                value={formData.project}
                onChange={(e) => setFormData(prev => ({ ...prev, project: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Sélectionner un projet</option>
                {projects.map(project => (
                  <option key={project.value} value={project.value}>
                    {project.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Équipe */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Équipe <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.team}
                onChange={(e) => setFormData(prev => ({ ...prev, team: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              >
                <option value="">Sélectionner une équipe</option>
                {teams.map(team => (
                  <option key={team.value} value={team.value}>
                    {team.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Tags */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tags (séparés par des virgules)
            </label>
            <input
              type="text"
              value={formData.tags}
              onChange={(e) => setFormData(prev => ({ ...prev, tags: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="important, rapport, 2024..."
            />
          </div>

          {/* Options avancées */}
          <div className="space-y-4">
            <div className="flex items-center">
              <input
                type="checkbox"
                id="isPublic"
                checked={formData.isPublic}
                onChange={(e) => setFormData(prev => ({ ...prev, isPublic: e.target.checked }))}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="isPublic" className="ml-2 text-sm text-gray-700">
                Rendre ce document public
              </label>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Synchroniser avec
              </label>
              <select
                value={formData.syncWith}
                onChange={(e) => setFormData(prev => ({ ...prev, syncWith: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="local">Stockage local uniquement</option>
                <option value="cloud">Cloud Storage</option>
                <option value="both">Local et Cloud</option>
              </select>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-6 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isSaving}
            >
              Annuler
            </Button>
            <Button
              type="submit"
              disabled={isSaving}
              className="flex items-center gap-2"
            >
              <Save className="w-4 h-4" />
              {isSaving ? 'Sauvegarde...' : 'Sauvegarder'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DocumentEditModal;
